import { useState } from 'react'
import { useDroppable } from '@dnd-kit/core'
import { Tag, MoreHorizontal, Pencil, Trash2, Palette, Plus, Check, X } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'

/** `key` maps to `documents:tags.colors.<key>` for the translated colour name. */
export const TAG_COLOR_PRESETS = [
  { value: '#6b7280', key: 'gray' },
  { value: '#ef4444', key: 'red' },
  { value: '#f97316', key: 'orange' },
  { value: '#f59e0b', key: 'yellow' },
  { value: '#22c55e', key: 'green' },
  { value: '#14b8a6', key: 'teal' },
  { value: '#3b82f6', key: 'blue' },
  { value: '#6366f1', key: 'indigo' },
  { value: '#a855f7', key: 'purple' },
  { value: '#ec4899', key: 'pink' },
] as const

export interface DocumentTag {
  id: string
  projectId: string
  name: string
  slug: string
  color: string
  createdAt: string
  updatedAt: string
}

export type TagFilter = 'all' | 'untagged' | string

interface DocumentTagSidebarProps {
  tags: DocumentTag[]
  selectedTag: TagFilter
  onSelectTag: (filter: TagFilter) => void
  onCreateTag: (name: string, color: string) => void
  onUpdateTag: (tagId: string, updates: { name?: string; color?: string }) => void
  onDeleteTag: (tagId: string) => void
}

function ColorDot({ color, size = 'sm' }: { color: string; size?: 'sm' | 'md' }) {
  return (
    <span
      className={cn(
        'inline-block rounded-full flex-shrink-0',
        size === 'sm' ? 'h-2.5 w-2.5' : 'h-3.5 w-3.5'
      )}
      style={{ backgroundColor: color }}
    />
  )
}

function ColorPicker({ value, onChange }: { value: string; onChange: (color: string) => void }) {
  const { t } = useTranslation('documents')
  return (
    <div className="flex gap-1.5 flex-wrap">
      {TAG_COLOR_PRESETS.map((preset) => (
        <button
          key={preset.value}
          type="button"
          onClick={() => onChange(preset.value)}
          className={cn(
            'h-6 w-6 rounded-full border-2 transition-transform hover:scale-110',
            value === preset.value ? 'border-foreground scale-110' : 'border-transparent'
          )}
          style={{ backgroundColor: preset.value }}
          title={t(`tags.colors.${preset.key}`)}
        />
      ))}
    </div>
  )
}

function DroppableTagRow({
  id,
  isSelected,
  onClick,
  className,
  children,
}: {
  id: string
  isSelected: boolean
  onClick: () => void
  className?: string
  children: React.ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: { type: 'tag', tagId: id },
  })

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-sm transition-colors',
        isSelected
          ? 'bg-accent text-accent-foreground font-medium'
          : 'text-foreground hover:bg-accent',
        isOver && !isSelected && 'ring-2 ring-primary bg-accent',
        className
      )}
    >
      {children}
    </button>
  )
}

export function DocumentTagSidebar({
  tags,
  selectedTag,
  onSelectTag,
  onCreateTag,
  onUpdateTag,
  onDeleteTag,
}: DocumentTagSidebarProps) {
  const { t } = useTranslation('documents')
  const [showNewTagInput, setShowNewTagInput] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#3b82f6')

  // Rename state
  const [renamingTag, setRenamingTag] = useState<DocumentTag | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // Recolor state
  const [recoloringTag, setRecoloringTag] = useState<DocumentTag | null>(null)
  const [recolorValue, setRecolorValue] = useState('')

  // Delete confirmation
  const [deletingTag, setDeletingTag] = useState<DocumentTag | null>(null)

  const handleCreateTag = () => {
    if (!newTagName.trim()) return
    onCreateTag(newTagName.trim(), newTagColor)
    setNewTagName('')
    setNewTagColor('#3b82f6')
    setShowNewTagInput(false)
  }

  const handleRenameTag = () => {
    if (!renamingTag || !renameValue.trim()) return
    onUpdateTag(renamingTag.id, { name: renameValue.trim() })
    setRenamingTag(null)
  }

  const handleRecolorTag = () => {
    if (!recoloringTag) return
    onUpdateTag(recoloringTag.id, { color: recolorValue })
    setRecoloringTag(null)
  }

  const handleDeleteTag = () => {
    if (!deletingTag) return
    onDeleteTag(deletingTag.id)
    setDeletingTag(null)
  }

  return (
    <div className="w-56 flex flex-col overflow-hidden">
      <div className="p-2 space-y-0.5 flex-1 overflow-y-auto">
        {/* Fixed filters */}
        <button
          type="button"
          onClick={() => onSelectTag('all')}
          className={cn(
            'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-sm transition-colors',
            selectedTag === 'all'
              ? 'bg-accent text-accent-foreground font-medium'
              : 'text-foreground hover:bg-accent'
          )}
        >
          <Tag className="h-3.5 w-3.5" />
          {t('tags.all')}
        </button>
        <DroppableTagRow
          id="__untagged__"
          isSelected={selectedTag === 'untagged'}
          onClick={() => onSelectTag('untagged')}
        >
          <span className="h-2.5 w-2.5 rounded-full border border-muted-foreground/40 flex-shrink-0" />
          {t('tags.untagged')}
        </DroppableTagRow>

        {tags.length > 0 && <div className="border-t border-border my-1.5" />}

        {/* Tag list */}
        {tags.map((tag) => (
          <div key={tag.id} className="group flex items-center">
            <DroppableTagRow
              id={tag.id}
              isSelected={selectedTag === tag.id}
              onClick={() => onSelectTag(tag.id)}
              className="flex-1 min-w-0"
            >
              <ColorDot color={tag.color} />
              <span className="truncate">{tag.name}</span>
            </DroppableTagRow>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="p-1 rounded hover:bg-accent text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-40">
                <DropdownMenuItem
                  onClick={() => {
                    setRenamingTag(tag)
                    setRenameValue(tag.name)
                  }}
                >
                  <Pencil className="h-4 w-4 mr-2" />
                  {t('tags.rename')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setRecoloringTag(tag)
                    setRecolorValue(tag.color)
                  }}
                >
                  <Palette className="h-4 w-4 mr-2" />
                  {t('tags.recolor')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setDeletingTag(tag)} className="text-destructive">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('tags.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ))}
      </div>

      {/* New tag input */}
      <div className="p-2 border-t border-border">
        {showNewTagInput ? (
          <div className="space-y-2">
            <input
              type="text"
              autoFocus
              maxLength={25}
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder={t('tags.namePlaceholder')}
              className="w-full h-8 px-2 text-sm rounded-md border border-border bg-background"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateTag()
                if (e.key === 'Escape') setShowNewTagInput(false)
              }}
            />
            <ColorPicker value={newTagColor} onChange={setNewTagColor} />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="flex-1"
                onClick={() => setShowNewTagInput(false)}
              >
                <X className="h-3.5 w-3.5 mr-1" />
                {t('tags.cancel')}
              </Button>
              <Button
                size="sm"
                className="flex-1"
                onClick={handleCreateTag}
                disabled={!newTagName.trim()}
              >
                <Check className="h-3.5 w-3.5 mr-1" />
                {t('tags.create')}
              </Button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNewTagInput(true)}
            className="flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-sm text-muted-foreground hover:bg-accent transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('tags.new')}
          </button>
        )}
      </div>

      {/* Rename dialog */}
      <Dialog open={!!renamingTag} onOpenChange={(open) => !open && setRenamingTag(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tags.renameDialog.title')}</DialogTitle>
          </DialogHeader>
          <input
            type="text"
            maxLength={25}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            className="w-full h-10 px-3 rounded-md border border-border bg-background text-sm"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRenameTag()
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenamingTag(null)}>
              {t('tags.cancel')}
            </Button>
            <Button onClick={handleRenameTag} disabled={!renameValue.trim()}>
              {t('tags.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recolor dialog */}
      <Dialog open={!!recoloringTag} onOpenChange={(open) => !open && setRecoloringTag(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tags.recolorDialog.title')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="tags.recolorDialog.description"
                values={{ name: recoloringTag?.name ?? '' }}
                components={{ strong: <strong /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <ColorPicker value={recolorValue} onChange={setRecolorValue} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecoloringTag(null)}>
              {t('tags.cancel')}
            </Button>
            <Button onClick={handleRecolorTag}>{t('tags.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deletingTag} onOpenChange={(open) => !open && setDeletingTag(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('tags.deleteDialog.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <Trans
              t={t}
              i18nKey="tags.deleteDialog.description"
              values={{ name: deletingTag?.name ?? '' }}
              components={{ strong: <strong /> }}
            />
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingTag(null)}>
              {t('tags.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDeleteTag}>
              {t('tags.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
